import * as globals from '@wandb/weave/common/css/globals.styles';
import Color from 'color';
import type uPlot from 'uplot';

import {formatXAxisNonTime, formatYAxis} from '../../../util/plotHelpers/axis';
import {
  getAxisStyleForFontSize,
  type PlotFontSize,
} from '../../../util/plotHelpers/plotFontSize';
import {type Line, type Timestep} from '../../../util/plotHelpers/types';
import {getStrokeDashArrayNonSvg, getStrokeDetail} from '../lineUtils';
import {type DomainMaybe} from '../types';
import {
  createDrawIssueLinesHook,
  type IssueLinesConfig,
} from './drawIssueLines';
import {
  createDrawNonFiniteMarkersHook,
  getNonFiniteMarkers,
  type NonFiniteMarker,
  type NonFiniteMarkerState,
} from './drawNonFiniteMarkers';
import {getBands} from './getBands';
import {getDomain} from './getDomain';
import {getIncrs} from './getIncrs';
import {type LineNameMap, type SeriesEntry} from './getSeriesEntries';
import {
  getuPlotLineGap,
  getuPlotTickFontSizePx,
  getuPlotTickGapPx,
  getuPlotTickSizePx,
  getuPlotXAxisTickValuesSizePx,
  getuPlotYAxisTickValuesSizePx,
} from './margins';

const GRID_LINE_WIDTH_PX = 1;
export const getGridLineColor = (isDarkMode: boolean) => {
  return isDarkMode
    ? Color(globals.MOON_750).rgb().string()
    : Color(globals.MOON_250).rgb().string();
};

export type NonFiniteMarkerContext = {
  markers: NonFiniteMarker[];
  stateRef: React.RefObject<NonFiniteMarkerState>;
};

// uPlot does not render a visible mark for a line series unless points are enabled.
// This callback shows a marker only when the current visible range contains exactly
// one finite point, which preserves the normal line-only rendering for dense series.
export function shouldShowSingleVisiblePoint(
  u: uPlot,
  seriesIdx: number,
  idx0: number,
  idx1: number
): boolean {
  const yValues = u.data[seriesIdx] as ArrayLike<number | null | undefined>;
  let finitePointCount = 0;

  for (let i = idx0; i <= idx1; i++) {
    const y = yValues[i];

    if (y != null && Number.isFinite(y) && ++finitePointCount > 1) {
      return false;
    }
  }

  return finitePointCount === 1;
}

export function getUPlotOptions(
  seriesEntries: (SeriesEntry | null)[],
  lineNameMap: LineNameMap,
  lines: Line[],
  nonFiniteMarkerStateRef: React.RefObject<NonFiniteMarkerState>,
  issueLinesConfig: IssueLinesConfig,
  width: number,
  height: number,
  xDomain: DomainMaybe,
  yDomain: DomainMaybe,
  xAxisIsDatetime: boolean | undefined,
  xLogScale: boolean | undefined,
  yLogScale: boolean | undefined,
  fontSize: PlotFontSize,
  timestep: Timestep | undefined,
  isDarkMode: boolean,
  isOverlay: boolean = false
): uPlot.Options {
  const bands = getBands(seriesEntries, lineNameMap);

  // Handle infinite/NaN markers
  const nonFiniteMarkers = getNonFiniteMarkers(lines);
  const drawNonFiniteMarkers = createDrawNonFiniteMarkersHook(
    nonFiniteMarkers,
    nonFiniteMarkerStateRef
  );

  // Handle issue lines
  const drawIssueLines = createDrawIssueLinesHook(issueLinesConfig);

  // First series config is for x-axis (empty object)
  // Then one config per line
  const seriesConfigs: uPlot.Series[] = [
    ...seriesEntries.map(entry => {
      if (entry == null) {
        // x-axis series
        return {};
      }
      const {type, line} = entry;
      const baseColor = line.color ?? 'red';
      const strokeDashArray = getStrokeDashArrayNonSvg(line.mark)?.map(
        num => num * window.devicePixelRatio
      );
      const {strokeWidth: lineStrokeWidth} = getStrokeDetail(
        line.color,
        isOverlay, // the axes are hidden in the overlay layer which is the highlighted layer
        line.lineWidth
      );

      if (type === 'band-min' || type === 'band-max') {
        // Band boundary series - needs visible stroke for uPlot bands to work
        return {
          show: true,
          spanGaps: true,
          stroke: Color(baseColor).rgb().string(),
          width: lineStrokeWidth,
          label: line.name,
          points: {show: false},
          sorted: 1, // Series.Sorted.Ascending - we pre-sort the data in getAlignedData, which allows some optimizations for uplot
        };
      } else {
        // Primary line series - full opacity. Dimming is handled by the useEffect in LinePlotuPlot.
        const stroke = Color(baseColor).rgb().string();
        return {
          show: true,
          spanGaps: true,
          stroke,
          width: lineStrokeWidth,
          label: line.name,
          sorted: 1, // Series.Sorted.Ascending - we pre-sort the data in getAlignedData, which allows some optimizations for uplot
          dash: strokeDashArray,
          points: {
            show: shouldShowSingleVisiblePoint,
            size: 4,
            width: 1,
            stroke,
            fill: stroke,
          },
        };
      }
    }),
  ];

  const tickLabelStyle = getAxisStyleForFontSize(fontSize);
  const fontString = `${getuPlotTickFontSizePx(fontSize)}px ${tickLabelStyle.fontFamily}`;

  const gridLineColor = getGridLineColor(isDarkMode);
  const sharedAxisOptions = {
    gap: getuPlotTickGapPx(),
    lineGap: getuPlotLineGap(),
    ticks: {
      show: true,
      size: getuPlotTickSizePx(),
      stroke: gridLineColor,
      width: GRID_LINE_WIDTH_PX,
    },
    font: fontString,
    border: {show: true, stroke: gridLineColor, width: GRID_LINE_WIDTH_PX},
  };

  const result: uPlot.Options = {
    width: width ?? 400,
    height: height ?? 400,
    series: seriesConfigs,
    bands,
    padding: [8, 12, 0, 0], // uPlot defaults to extra padding we don't need
    axes: [
      // x-axis
      {
        stroke: tickLabelStyle.fill as string, // this is a string, not just any CSSProperties
        values: xAxisIsDatetime
          ? undefined
          : (_u: uPlot, splits: number[]) => {
              if (xDomain[0] == null || xDomain[1] == null) {
                return splits;
              }
              const formatter = formatXAxisNonTime(
                xLogScale ? 'log' : 'linear',
                xDomain[0]!,
                xDomain[1]!,
                timestep
              );
              if (formatter == null) {
                return splits;
              }
              const formattedSplits = splits.map(split =>
                split === null ? null : formatter(split)
              );
              // We only care about non-null splits appearing the same.
              const filteredFormattedSplits = formattedSplits.filter(
                split => split != null
              );
              // Just like in react-vis https://github.com/wandb/core/pull/22703
              if (
                new Set(filteredFormattedSplits).size !==
                filteredFormattedSplits.length
              ) {
                // duplicate x values, so actually just return the original splits
                return splits;
              }
              return formattedSplits;
            },
        grid: {
          show: false,
        },
        size: getuPlotXAxisTickValuesSizePx(fontSize),
        incrs: getIncrs(xAxisIsDatetime ?? false),
        ...sharedAxisOptions,
      },
      // y-axis
      {
        stroke: tickLabelStyle.fill as string, // this is a string, not just any CSSProperties
        values: (_u: uPlot, splits: number[]) => {
          return splits.map(split =>
            split == null ? null : formatYAxis(split)
          );
        },
        size: getuPlotYAxisTickValuesSizePx(fontSize),
        grid: {
          show: true,
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
        },
        incrs: getIncrs(false),
        ...sharedAxisOptions,
      },
    ],
    scales: {
      x: {
        time: xAxisIsDatetime ?? false,
        distr: xLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(xDomain, xLogScale ?? false),
      },
      y: {
        distr: yLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(yDomain, yLogScale ?? false),
      },
    },
    legend: {
      show: false,
    },
    hooks: {
      draw: [drawIssueLines, drawNonFiniteMarkers],
    },
    ms: 1, // uPlot uses seconds by default, but the Line data is in milliseconds
  };
  return result;
}
